import { useContext, useRef } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { useIntl } from "react-intl";
import { InlineEdit, displayValue } from "@/components/design/InlineEdit";
import { Select } from "@/components/design/Select";
import { Switch } from "@/components/design/Switch";
import type { FieldOption } from "@/lib/field-util";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { todayIso } from "@/lib/date-instant";
import { EditContext, ShareEditContext } from "./context";
import { useProfileDateWarning, useProfileFieldWarning } from "./validation";
import { MergeDateWarning } from "@/components/save/MergeDateWarning";
import { MergeFieldWarning } from "@/components/save/MergeFieldWarning";
import { ProfileMergeField } from "@/components/save/ProfileMergeField";
import { useProfileMergeStore, detailDiffFor } from "@/stores/profile-merge-store";
import { ImageUpload } from "@/components/common/ImageUpload";

// A field hosting an import-review card takes the FULL grid row. The card is a
// whole editing surface — compared pair plus the keep/use button pair — and the
// two-column business-card grid leaves a cell only ~160px wide at 1280x720,
// where the buttons no longer fit beside each other (spark#174). Only from `@sm`
// up: that is where the host grids switch to two columns, and col-span-2 in a
// one-column grid would invent a second column.
const REVIEW_SPAN = "@sm:col-span-2";

export function AvatarUpload({
  avatarUrl,
  name,
  editable,
  hasAvatar,
  onSelectFile,
  onRemove,
}: {
  avatarUrl: string | null;
  name: string;
  editable: boolean;
  hasAvatar: boolean;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const intl = useIntl();

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onSelectFile(file);
  }

  const changeLabel = intl.formatMessage({
    id: "profile.fields.changePhoto",
    defaultMessage: "Change photo",
  });

  // The round control, the hover overlay and the remove link are ImageUpload's — the company logo is
  // the same interaction and now draws from the same place. What stays here is what is specific to a
  // PERSON: the hidden file input, the initials fallback and its placeholder wash.
  return (
    <ImageUpload
      editable={editable}
      hoverLabel={changeLabel}
      onActivate={() => inputRef.current?.click()}
      remove={
        hasAvatar
          ? {
              label: intl.formatMessage({
                id: "profile.fields.removePhoto",
                defaultMessage: "Remove photo",
              }),
              onRemove,
            }
          : null
      }
      style={avatarUrl ? undefined : AVATAR_PLACEHOLDER_STYLE}
    >
      <span className="flex h-32 w-32 items-center justify-center font-serif text-4xl">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name || intl.formatMessage({ id: "profile.fields.avatarAlt", defaultMessage: "Avatar" })}
            className="h-full w-full object-cover"
          />
        ) : (
          avatarInitials(name)
        )}
      </span>
      {/* Only when it can be used: a hidden input on a read-only avatar is a control nobody asked
          for, and the test below is what noticed it going in. */}
      {editable ? (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      ) : null}
    </ImageUpload>
  );
}
function FieldHeader({
  label,
  shared,
  onSharedChange,
  className = "",
  shareId,
}: {
  label: string;
  shared: boolean;
  onSharedChange?: (next: boolean) => void;
  className?: string;
  shareId?: string;
}) {
  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="field-label">{label}</div>
      <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
    </div>
  );
}

export function ShareToggle({
  shared,
  onChange,
  id,
}: {
  shared: boolean;
  onChange?: (next: boolean) => void;
  id?: string;
}) {
  const intl = useIntl();
  const { mounted, exiting } = useContext(ShareEditContext);
  if (!onChange || !mounted) return null;
  return (
    <Switch
      id={id}
      checked={shared}
      onChange={onChange}
      ariaLabel={intl.formatMessage(
        shared
          ? { id: "common.share.ariaActive", defaultMessage: "Sharing active" }
          : { id: "common.share.ariaInactive", defaultMessage: "Not sharing" },
      )}
      label={{
        on: intl.formatMessage({ id: "common.share.on", defaultMessage: "Share" }),
        off: intl.formatMessage({ id: "common.share.off", defaultMessage: "Private" }),
      }}
      exiting={exiting}
    />
  );
}

// Forces the share toggle visible regardless of the global "Sichtbarkeit
// bearbeiten" mode. Used inside an entry's edit panel: editing a single entry
// should always expose its share control (the top button only toggles them
// all at once for convenience).
export function ForcedShare({ children }: { children: ReactNode }) {
  return (
    <ShareEditContext.Provider value={{ mounted: true, exiting: false }}>
      <div className="flex justify-end">{children}</div>
    </ShareEditContext.Provider>
  );
}

export function BasicField({
  label,
  value,
  onChange,
  readOnly = false,
  type = "text",
  min,
  max,
  shared = false,
  onSharedChange,
  suffix,
  id,
  mergeKey,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  type?: "text" | "date" | "number" | "email" | "tel";
  min?: number | string;
  max?: number | string;
  // Omit both to render a field with no share toggle (e.g. jobTitle, which has
  // no shared* attribute in genesis). ShareToggle hides itself without onChange.
  shared?: boolean;
  onSharedChange?: (next: boolean) => void;
  suffix?: string | null;
  id?: string;
  // The genesis field key (e.g. "bio", "profession"). Two roles: an in-line
  // LinkedIn merge affordance renders under the field while an import preview is
  // active (nothing otherwise, so it is safe to pass unconditionally), and it is
  // the key the length/format validation is looked up under. A field with no
  // mergeKey is therefore also unvalidated — which is exactly right for the three
  // base-data fields genesis caps nowhere (miscellaneous, currentTasks,
  // countryState). See lib/import-field-validation.ts.
  mergeKey?: string;
}) {
  const editable = useContext(EditContext);
  // Floating-label pattern: while the field is empty the label sits inside
  // the field as a muted placeholder; once a value is present the label moves
  // above as the small `.field-label` eyebrow and the value takes the
  // prominent position.
  const hasValue = Boolean(value);
  const shareId = id ? `${id}-share` : undefined;
  // While LinkedIn proposes a change, the field is locked and the editable
  // compared pair is rendered below it. The field itself intentionally keeps the
  // same border/outline treatment as every other profile field. The review is
  // an editing surface: on a non-editable profile (viewing another person while
  // one's own preview is active) no merge affordance may appear.
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While LinkedIn proposes a change for this field, the ORIGINAL value is
  // locked: it keeps showing what is saved in the system, and all editing
  // happens on the compared pair in the merge card below. (It would also drift
  // the pristine value the save is diffed against.)
  const showStatic = readOnly || !editable || mergeDiff != null;
  // Length/format warning for the plain editor. While a merge card is open the field is locked and the
  // card carries its own warning, so this one stays out of the way. An unkeyed field never warns —
  // see the `mergeKey` note above.
  const warn = useProfileFieldWarning(mergeKey ?? "", value);
  const warning = showStatic ? null : warn.error;
  // A date field may not hold a future date (the only one today is the birthday). `max` gives the
  // native picker its ceiling; dateWarn explains a value that got in anyway — pasted, typed into the
  // keyboard-accessible segments, or already stored. A caller can still pass its own `max`.
  const dateWarn = useProfileDateWarning(type === "date" ? value : "");
  const dateWarning = showStatic ? null : dateWarn.error;
  const resolvedMax = type === "date" && max === undefined ? todayIso() : max;
  return (
    <div className={mergeDiff ? REVIEW_SPAN : undefined}>
      {hasValue ? (
        <FieldHeader label={label} shared={shared} onSharedChange={onSharedChange} shareId={shareId} />
      ) : null}
      <div className={`flex items-baseline gap-2 ${hasValue ? "mt-0.5" : ""}`}>
        {showStatic ? (
          hasValue ? (
            <div className="field-value text-ink/80">{displayValue(value, type)}</div>
          ) : (
            <div className="field-value min-w-0 flex-1 text-ink/30">{label}</div>
          )
        ) : (
          <InlineEdit
            id={id}
            value={value}
            onChange={(next) => {
              warn.onEdit();
              dateWarn.onEdit();
              onChange(next);
            }}
            onCommit={() => {
              warn.onBlur();
              dateWarn.onBlur();
            }}
            type={type}
            min={min}
            max={resolvedMax}
            placeholder={label}
            className="field-value min-w-0 flex-1"
            inputClassName="field-value"
          />
        )}
        {/* No eyebrow header in the empty state, so the toggle rides the
            placeholder line - ml-auto keeps it right-aligned, matching the
            header toggle of filled fields. */}
        {!hasValue ? (
          <div className="ml-auto shrink-0">
            <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
          </div>
        ) : null}
        {suffix ? (
          <span className="field-meta whitespace-nowrap text-ink/45">{suffix}</span>
        ) : null}
      </div>
      {mergeKey ? <MergeFieldWarning fieldKey={mergeKey} error={warning} /> : null}
      <MergeDateWarning error={dateWarning} />
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} type={type} /> : null}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  shared,
  onSharedChange,
  id,
  mergeKey,
  mergeFormat,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: FieldOption[];
  shared: boolean;
  onSharedChange?: (next: boolean) => void;
  id?: string;
  // A reference field (gender, marital status, country) whose stored value is an
  // id. During an import merge the field locks and a read-only merge card renders
  // below, showing the label via `mergeFormat` and committing the id.
  mergeKey?: string;
  mergeFormat?: (value: string) => string;
}) {
  const editable = useContext(EditContext);
  const hasValue = Boolean(value);
  const shareId = id ? `${id}-share` : undefined;
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While the import proposes a change the select is locked (like BasicField):
  // the original keeps showing the saved value and the choice happens in the card.
  const selectEditable = editable && mergeDiff == null;
  return (
    <div className={mergeDiff ? REVIEW_SPAN : undefined}>
      {hasValue ? (
        <FieldHeader label={label} shared={shared} onSharedChange={onSharedChange} shareId={shareId} />
      ) : null}
      <div className={`flex items-baseline gap-2 ${hasValue ? "mt-0.5" : ""}`}>
        <div className="min-w-0 flex-1">
          <Select
            id={id}
            value={value}
            onChange={onChange}
            options={options}
            editable={selectEditable}
            placeholder={label}
            variant="plain"
          />
        </div>
        {!hasValue ? (
          <div className="ml-auto shrink-0">
            <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
          </div>
        ) : null}
      </div>
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} editable={false} format={mergeFormat} /> : null}
    </div>
  );
}

export function MultiCountryField({
  label,
  value,
  onChange,
  options,
  shared,
  onSharedChange,
  id,
  mergeKey,
  mergeFormat,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: FieldOption[];
  shared: boolean;
  onSharedChange: (next: boolean) => void;
  id?: string;
  // A multi-value id field (citizenship). During an import merge the chips lock
  // and a read-only merge card renders below (the incoming side is the merged
  // id-list), showing labels via `mergeFormat` and committing the ids.
  mergeKey?: string;
  mergeFormat?: (value: string) => string;
}) {
  const editable = useContext(EditContext);
  const intl = useIntl();
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While the import proposes a change the chips are locked; the card drives it.
  const chipsEditable = editable && mergeDiff == null;
  const ids = value ? String(value).split(",").filter(Boolean) : [];
  const labelFor = (cid: string) => options.find((o) => o.value === cid)?.label || cid;
  const remaining = options.filter((o) => !ids.includes(o.value));
  const add = (cid: string) => {
    if (cid && !ids.includes(cid)) onChange([...ids, cid].join(","));
  };
  const remove = (cid: string) => onChange(ids.filter((x) => x !== cid).join(","));
  return (
    <div id={id} className={mergeDiff ? REVIEW_SPAN : undefined}>
      <FieldHeader
        label={label}
        shared={shared}
        onSharedChange={onSharedChange}
        className="mb-1"
        shareId={id ? `${id}-share` : undefined}
      />
      <div className="flex flex-wrap items-center gap-2">
        {ids.length === 0 && !chipsEditable ? (
          <span className="field-value text-ink/30">–</span>
        ) : null}
        {ids.map((cid) => (
          <span
            key={cid}
            className="field-meta inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 ring-1 ring-ink/10"
          >
            {labelFor(cid)}
            {chipsEditable ? (
              <button
                type="button"
                onClick={() => remove(cid)}
                className="text-ink/40 hover:text-accent"
                aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
              >
                ✕
              </button>
            ) : null}
          </span>
        ))}
        {chipsEditable && remaining.length > 0 ? (
          <Select
            value=""
            onChange={add}
            options={remaining}
            placeholder={intl.formatMessage({ id: "profile.fields.addCountry", defaultMessage: "+ Country" })}
            variant="plain"
          />
        ) : null}
      </div>
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} editable={false} format={mergeFormat} /> : null}
    </div>
  );
}
